"use client";

import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import {
  ShoppingCart,
  X,
  Calendar,
  Stethoscope,
  Mail,
  Repeat,
  Info,
  HandHeartIcon,
  BandageIcon,
} from "lucide-react";
import { track } from "@lib/track";
import { useCart } from "../data/CartContext";
import ScheduleCall from "@components/ScheduleCall";
import ScheduleTreatment from "@components/ScheduleTreatment";
import TrackedLink from "@components/TrackedLink";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

// Define expected detail shape
interface QuickActionsDetail {
  from?: string;
  serviceId?: string;
  serviceName?: string;
}

/** iOS-safe body scroll lock (prevents background scroll/taps) */
function useLockBodyScroll(locked: boolean) {
  useLayoutEffect(() => {
    const body = document.body;
    const scrollY = window.scrollY;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      overflow: body.style.overflow,
      width: body.style.width,
      touchAction: body.style.touchAction,
    } as const;

    if (locked) {
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.width = "100%";
      body.style.overflow = "hidden";
      body.style.touchAction = "none";
    } else {
      body.style.position = prev.position || "";
      body.style.top = prev.top || "";
      body.style.width = prev.width || "";
      body.style.overflow = prev.overflow || "";
      body.style.touchAction = prev.touchAction || "";
      if (prev.top) {
        const y = -parseInt(prev.top || "0", 10);
        window.scrollTo(0, y || 0);
      }
    }

    return () => {
      body.style.position = prev.position || "";
      body.style.top = prev.top || "";
      body.style.width = prev.width || "";
      body.style.overflow = prev.overflow || "";
      body.style.touchAction = prev.touchAction || "";
      if (prev.top) {
        const y = -parseInt(prev.top || "0", 10);
        window.scrollTo(0, y || 0);
      }
    };
  }, [locked]);
}

type CartItem = { id: string; title: string; qty: number; totalPrice: number };

type ActionDef = {
  key: string;
  href: string;
  label: string;
  icon: React.ReactNode;
  primary?: boolean;
  length?: string;
  charge?: string;
  duration?: number;
  meetingType?: string;
};

export default function FloatingCart() {
  const { cart } = useCart() as { cart: CartItem[] };
  
  const router = useRouter();
  const pathname = usePathname();

  const [open, setOpen] = useState(false); // cart drawer
  const [actionsOpen, setActionsOpen] = useState(false); // quick actions drawer
  const [activeWidget, setActiveWidget] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<ActionDef | null>(null);

  // NEW: nudge state -> makes cart fab pulse when items are present or when just added
  const [nudge, setNudge] = useState(false);

  // Lock background whenever any overlay is open
  useLockBodyScroll(open || actionsOpen || modalOpen);

  const redirectedRef = useRef(false);

  const itemCount = useMemo(
    () => cart?.reduce((sum, item) => sum + (item?.qty ?? 1), 0) ?? 0,
    [cart]
  );

  // 🔁 REMOVED: auto-open when there are items. (Was causing annoyance on back nav)
  // useEffect(() => { if (itemCount > 0) setOpen(true); }, [itemCount]);

  // ✅ Instead: gently highlight the cart button when items exist
  useEffect(() => {
    setNudge(itemCount > 0);
  }, [itemCount]);

  // ✅ Also flash the nudge briefly on add-to-cart events
  useEffect(() => {
    const onAdd = () => {
      setNudge(true);
      const t = setTimeout(() => setNudge(itemCount > 0), 2000);
      return () => clearTimeout(t);
    };
    window.addEventListener("add-to-cart" as string, onAdd);
    return () => window.removeEventListener("add-to-cart" as string, onAdd);
  }, [itemCount]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handlerQA = (e: Event) => {
      setActionsOpen(true);
      setOpen(false);
      try {
        const customEvent = e as CustomEvent<QuickActionsDetail>;
        const detail = customEvent.detail ?? {};

        window.dataLayer?.push({
          event: "quick_actions_open",
          from: detail.from || "external",
          service_id: detail.serviceId,
          service_name: detail.serviceName,
        });

        track(
          "click", // Event name
          {
            label: "Quick Actions (external trigger)", // Metadata: label
            component: "FloatingCart", // Metadata: component
          },
          {
            event: "click", // Event name for TrackEvent
            label: "Quick Actions (external trigger)", // Event label
            component: "FloatingCart", // Component name
            meta: detail as Record<string, unknown>, // Metadata: additional details
          }
        );
      } catch {}
    };
    const handlerCart = () => {
      setOpen(true);
      try {
        track(
          "click", // Event name
          {
            label: "Cart (external trigger)", // Metadata: label
            component: "FloatingCart", // Metadata: component
          },
          {
            event: "click", // Event name for TrackEvent
            label: "Cart (external trigger)", // Event label
            component: "FloatingCart", // Component name
          }
        );
      } catch {}
    };
    window.addEventListener("floatingCart:openQuickActions", handlerQA);
    window.addEventListener("floatingCart:openCart", handlerCart);
    return () => {
      window.removeEventListener("floatingCart:openQuickActions", handlerQA);
      window.removeEventListener("floatingCart:openCart", handlerCart);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const isEmpty = (cart?.length ?? 0) === 0;
    if (!isEmpty) return;
    const alreadyOnShop = pathname?.startsWith("/shop");
    if (alreadyOnShop) return;
    const sessionKey = "floatingCart:emptyRedirected";
    if (redirectedRef.current) return;
    if (typeof window !== "undefined" && window.sessionStorage?.getItem(sessionKey) === "1") return;
    redirectedRef.current = true;
    const t = setTimeout(() => {
      setOpen(false);
      try {
        if (typeof window !== "undefined") window.sessionStorage?.setItem(sessionKey, "1");
      } catch {}
      router.push("/shop");
      track(
        "redirect", // Event name
        {
          label: "Redirect to Shop (empty cart)", // Metadata: label
          component: "FloatingCart", // Metadata: component
        },
        {
          event: "redirect", // Event name for TrackEvent
          label: "Redirect to Shop (empty cart)", // Event label
          component: "FloatingCart", // Component name
          // Uncomment the following lines if needed and ensure proper types:
          // path_from: pathname ?? "", // Metadata: path from
          // path_to: "/shop", // Metadata: path to
        }
      );
    }, 2000);
    return () => clearTimeout(t);
  }, [open, cart, pathname, router]);

  useEffect(() => {
    // Close everything on route change
    setOpen(false);
    setActionsOpen(false);
    setActiveWidget(null);
    setModalOpen(false);
    setModalAction(null);
  }, [pathname]);

  const feeConsultation = `£${process.env.NEXT_PUBLIC_CONSULTATION_FEE ?? "250"}`;
  const feeFollowUp = `£${process.env.NEXT_PUBLIC_FLEXIBLE_FEE ?? "80"}`;
  const feeBodhi = `£${process.env.NEXT_PUBLIC_BODHI_CLUB_FEE ?? "40"}`;
  const feeFarleigh = `£${process.env.NEXT_PUBLIC_FARLEIGH_METHOD_FEE ?? "600"}`;

  const actions: ReadonlyArray<ActionDef> = [
    {
      key: "discovery",
      href: "/workwithme/discoverycall#schedule-call",
      label: "Discovery",
      icon: <Calendar className="h-4 w-4" />,
      primary: true,
      length: "20 mins",
      charge: "Free",
      duration: 20,
      meetingType: "discovery",
    },
    {
      key: "farleigh",
      href: "/workwithme/farleighMethod#schedule-call",
      label: "Farleigh Method",
      icon: <HandHeartIcon className="h-4 w-4" />,
      length: "Per Month",
      charge: feeFarleigh,
      duration: 60,
      meetingType: "farleigh-method",
    },
    {
      key: "consultation",
      href: "/workwithme/consultation#schedule-call",
      label: "Consultation",
      icon: <Stethoscope className="h-4 w-4" />,
      length: "60 mins",
      charge: feeConsultation,
      duration: 60,
      meetingType: "full-consultation",
    },
    {
      key: "followup",
      href: "/workwithme/flexibleConsult#schedule-call",
      label: "Follow Up",
      icon: <Repeat className="h-4 w-4" />,
      length: "30 mins",
      charge: feeFollowUp,
      duration: 30,
      meetingType: "flexible",
    },
    {
      key: "bodhi",
      href: "/workwithme/align#schedule-call",
      label: "Bodhi Club",
      icon: <HandHeartIcon className="h-4 w-4" />,
      length: "Per Month",
      charge: feeBodhi,
      duration: 30,
      meetingType: "bodhi-club",
    },
    {
      key: "treatment",
      href: "/treatments",
      label: "Treatment",
      icon: <BandageIcon className="h-4 w-4" />,
      length: "Various",
      charge: "Various",
      duration: 45,
      meetingType: "Treatment",
    },
    {
      key: "contact",
      href: "/contactus",
      label: "Contact Us",
      icon: <Mail className="h-4 w-4" />,
    },
  ] as const;

  {/*}
  function trackQuickAction(action: string) {
    try {
      (window as { dataLayer?: { push: (data: unknown) => void } })?.dataLayer?.push({
        event: "quick_action_click",
        action,
        location: "floating_fab",
      });
    } catch {}
    try {
      track({
        event: "click",
        label: `Quick Action: ${action}`,
        component: "FloatingCart",
        meta: { location: "floating_fab" },
      });
    } catch {}
  } */}

  function openScheduleModal(action: ActionDef) {

    if (action.key === "contact") {
      setActionsOpen(false);
      router.push(action.href);
      return;
    }

    
    try {
      (window as { dataLayer?: { push: (data: unknown) => void } })?.dataLayer?.push({
        event: "booking_modal_opened",
        action: action.key,
        location: "quick_actions_drawer",
      });
    } catch {}
    try {
      track(
        "click", // Event name
        {
          label: `Booking Modal: ${action.key}`, // Metadata: label
          component: "FloatingCart", // Metadata: component
        },
        {
          event: "click", // Event name for TrackEvent
          label: `Booking Modal: ${action.key}`, // Event label
          component: "FloatingCart", // Component name
          meta: {
            location: "quick_actions_drawer", // Metadata: location
          },
        }
      );
    } catch {}
    setModalAction(action);
    setModalOpen(true);
  }

  return (
    <>
      {/* Quick Actions FAB */}
      <button
        onClick={() => {
          setActionsOpen((v) => !v);
          track(
            "click", // Event name
            {
              label: "Quick Actions Button", // Metadata: label
              component: "FloatingCart", // Metadata: component
            },
            {
              event: "click", // Event name for TrackEvent
              label: "Quick Actions Button", // Event label
              component: "FloatingCart", // Component name
            }
          );
        }}
        className="fixed right-4 z-50 rounded-full text-[var(--color-accent)] px-4 py-3 shadow-lg hover:border hover:border-[var(--color-accent)]"
        style={{ bottom: "calc(5rem + env(safe-area-inset-bottom))" }}
        aria-label="Quick Actions"
      >
        <Calendar className="h-6 w-6" />
      </button>

      {/* Cart FAB (pulses when items exist or just added) */}
      <button
        onClick={() => {
          if (itemCount === 0) {
            router.push("/shop");
            track(
              "click", // Event name
              {
                label: "Cart Button (from empty cart)", // Metadata: label
                component: "FloatingCart", // Metadata: component
              },
              {
                event: "click", // Event name for TrackEvent
                label: "Cart Button (from empty cart)", // Event label
                component: "FloatingCart", // Component name
              }
            );
            return;
          }
          setOpen((v) => !v);
        }}
        className={`fixed right-4 z-50 rounded-full px-4 py-3 shadow-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-hover)]
          ${nudge ? "animate-pulse bg-[var(--color-accent-hover)]" : "text-[var(--color-accent)] hover:border hover:border-[var(--color-accent)]"}
          text-white hover:opacity-90`}
        style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        aria-label="Open cart"
        aria-live="polite"
        aria-atomic="true"
        data-has-items={itemCount > 0}
      >
        <div className="flex items-center gap-1">
          <ShoppingCart className="h-6 w-6 text-[var(--color-accent)]" />
          {itemCount > 0 && <span className="ml-1 font-bold">({itemCount})</span>}
        </div>
      </button>

      {/* Quick Actions Drawer */}
      {actionsOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="flex-1 bg-black/40" onClick={() => setActionsOpen(false)} />
          <div className="w-full sm:w-96 bg-white shadow-xl p-4 flex flex-col relative">
            <button
              onClick={() => setActionsOpen(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
              aria-label="Close Quick Actions"
            >
              <X className="h-5 w-5" />
            </button>

            <h2 className="text-lg font-semibold mb-3 text-[var(--color-text-brand)]">Quick Scheduling Actions</h2>

            <ul className="space-y-3">
              {actions.map((action) => {
                const isOpen = activeWidget === action.key;
                return (
                  <li key={action.key} className="rounded-lg border border-[var(--color-text-brand)] overflow-hidden">
                    <div className="flex items-stretch">
                      {/* Left icon: info link */}
                      <TrackedLink
                        href={action.href}
                        event="nav_click"
                        label={`quick_action_info:${action.key}`}
                        component="FloatingCart"
                        className="group inline-flex items-center justify-center w-12 shrink-0 border-r border-[var(--color-text-brand)] text-[var(--color-text-brand)] hover:bg-[var(--color-brand-contrast)] focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-contrast)]"
                        aria-label={`More info about ${action.label}`}
                        // title removed as it is not defined in the expected prop types
                      >
                        <span className="relative inline-flex h-6 w-6 items-center justify-center">
                          <span className="transition-opacity group-hover:opacity-0">{action.icon}</span>
                          <Info className="absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100 h-5 w-5" />
                        </span>
                      </TrackedLink>

                      {/* Right button: open modal */}
                      <button
                        onClick={() => openScheduleModal(action)}
                        className={`flex-1 flex items-center gap-3 p-3 text-left hover:bg-[var(--color-brand-contrast)] focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-contrast)] ${
                          action.primary ? "font-semibold" : ""
                        }`}
                        aria-haspopup="dialog"
                      >
                        <div className="flex w-full items-center justify-between">
                          <div className="text-[var(--color-brand)]">
                            {action.label}
                            {(action.charge || action.length) && (
                              <p className="text-xs text-gray-500">
                                {action.charge ? `Cost: ${action.charge}` : ""}
                                {action.charge && action.length ? " • " : ""}
                                {action.length ? `Length: ${action.length}` : ""}
                              </p>
                            )}
                          </div>
                          {action.key !== "contact" && (
                            <div className="ml-6 my-auto text-[var(--color-brand)] flex flex-col items-center text-right leading-tight">
                              <span>Reserve</span>
                              <span>Now</span>
                            </div>
                          )}
                        </div>
                      </button>
                    </div>

                    {isOpen && (
                      <div className="border-t border-[var(--color-brand)] bg-gray-50 p-3 text-sm text-gray-600">
                        More details…
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            <button
              onClick={() => setActionsOpen(false)}
              className="mt-4 w-full text-center bg-gray-200 text-gray-700 py-2 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Booking Modal (scroll/tap safe) */}
      {modalOpen && modalAction && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center isolate"
          role="dialog"
          aria-modal="true"
          aria-labelledby="booking-title"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 z-0 bg-black/50" onClick={() => setModalOpen(false)} />

          {/* Modal Card */}
          <div className="relative z-10 w-full max-w-2xl mx-4 rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header (no scroll) */}
            <div className="flex items-center justify-between p-4 border-b shrink-0">
              <h3 id="booking-title" className="text-lg font-semibold text-[var(--color-brand)]">
                {modalAction.label} — Book a Time
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="text-gray-500 hover:text-gray-800"
                aria-label="Close booking modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Scroll Area (only this section scrolls) */}
            <div
              className="p-4 grow overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] [touch-action:pan-y]"
              onClick={(e) => e.stopPropagation()}
            >
              {modalAction.key === "treatment" ? (
                <ScheduleTreatment
                  duration={modalAction.duration ?? 45}
                  meetingType={modalAction.meetingType ?? "Treatment"}
                  treatmentTypes={[]}
                />
              ) : (
                <ScheduleCall
                  duration={modalAction.duration ?? 30}
                  meetingType={modalAction.meetingType ?? "Flexible-Consultation"}
                />
              )}
            </div>

            {/* Footer (no scroll) */}
            <div className="flex justify-end gap-2 p-4 border-t shrink-0">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cart Drawer */}
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
          <div className="w-full sm:w-80 bg-white shadow-xl p-4 flex flex-col relative">
            <button
              onClick={() => setOpen(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
              aria-label="Close Cart"
            >
              <X className="h-5 w-5" />
            </button>

            <h2 className="text-lg font-semibold mb-3 text-[var(--color-text-brand)]">Your Cart</h2>
            {(cart?.length ?? 0) === 0 ? (
              <p>Your cart is empty. Redirecting to the shop…</p>
            ) : (
              <ul className="flex-1 overflow-y-auto space-y-2 text-[var(--color-text-brand)]">
                {cart.map((item) => (
                  <li key={item.id} className="flex justify-between">
                    <span>
                      {item.qty} × {item.title}
                    </span>
                    <span>£{Number(item.totalPrice).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            )}

            <button
              onClick={() => setOpen(false)}
              className="mt-4 w-full text-center bg-gray-200 text-gray-700 py-2 rounded hover:bg-gray-300"
            >
              Close
            </button>

            {(cart?.length ?? 0) > 0 && (
              <div>
              <TrackedLink
                href="/shop/cart"
                event="nav_click"
                label="go_to_checkout"
                component="FloatingCart"
                onClick={() =>
                  track(
                    "click", // Event name
                    {
                      label: "Go to Checkout", // Metadata: label
                      component: "FloatingCart", // Metadata: component
                    },
                    {
                      event: "click", // Event name for TrackEvent
                      label: "Go to Checkout", // Event label
                      component: "FloatingCart", // Component name
                    }
                  )
                }
                className="mt-4 inline-block w-full text-center bg-[var(--color-brand)] text-white py-2 rounded"
              >
                Go to checkout
              </TrackedLink>

             
              </div>

            )}
          </div>
        </div>
      )}
    </>
  );
}
